import { useState } from 'react';
import { Brain, Zap, Target } from 'lucide-react';
import BusinessForm from './components/BusinessForm';
import GuideResult from './components/GuideResult';
import { generateBusinessGuide } from './lib/openai';

function App() {
  const [currentView, setCurrentView] = useState('landing'); // 'landing', 'form', 'result'
  const [generatedGuide, setGeneratedGuide] = useState('');
  const [lastFormData, setLastFormData] = useState(null);

  const handleStartGuide = () => {
    setCurrentView('form');
  };

  const handleGuideGenerated = (guide) => {
    setGeneratedGuide(guide);
    setCurrentView('result');
  };

  const handleBackToForm = () => {
    setCurrentView('form');
  };

  const handleBackToLanding = () => {
    setCurrentView('landing');
  };

  const handleRegenerate = async () => {
    if (lastFormData) {
      try {
        const newGuide = await generateBusinessGuide(lastFormData);
        setGeneratedGuide(newGuide);
      } catch (error) {
        console.error('재생성 오류:', error);
      }
    }
  };

  // Landing Page
  if (currentView === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="bg-primary rounded-full p-4">
                <Brain className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold gradient-text mb-4">
              AI Business Guide Maker
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
              경영컨설팅은 끝났다. AI가 5분 만에 당신의 비즈니스 가이드를 생성합니다.
            </p>

            {/* 책 이미지 섹션 */}
            <div className="flex justify-center items-center gap-4 mt-8">
              <a
                href="https://search.shopping.naver.com/book/catalog/56947386015?cat_id=50005827&frm=PBOKMOD&query=%EA%B2%BD%EC%98%81%EC%BB%A8%EC%84%A4%ED%8C%85%EC%9D%80+%EB%81%9D%EB%82%AC%EB%8B%A4&NaPm=ct%3Dmg1m0sds%7Cci%3D8c87413a4bfd29d68d2d76afc282a9ce7a4306cc%7Ctr%3Dboknx%7Csn%3D95694%7Chk%3D1fa8c0e795bf3f5edb2f20fe467a07e57715e09b"
                target="_blank"
                rel="noopener noreferrer"
                className="relative group cursor-pointer"
              >
                <img
                  src="/book-cover.jpg"
                  alt="경영컨설팅은 끝났다 책 표지"
                  className="w-32 h-48 object-cover rounded-lg shadow-xl transform transition-transform group-hover:scale-105"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* 이미지 로드 실패시 대체 표시 */}
                <div className="w-32 h-48 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg shadow-xl flex items-center justify-center p-4"
                     style={{display: 'none'}}>
                  <div className="text-white text-center">
                    <p className="text-sm font-bold mb-2">경영컨설팅은</p>
                    <p className="text-lg font-bold">끝났다</p>
                    <p className="text-xs mt-2">AI 시대의 비즈니스 가이드</p>
                  </div>
                </div>
                {/* 호버 시 구매 안내 오버레이 */}
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <div className="text-white text-center">
                    <p className="text-sm font-bold">📚 도서 구매</p>
                    <p className="text-xs mt-1">클릭하여 구매하기</p>
                  </div>
                </div>
              </a>
              <div className="text-left max-w-xs">
                <p className="text-sm text-gray-500 mb-1">베스트셀러 원작</p>
                <p className="text-lg font-semibold text-gray-800">「경영컨설팅은 끝났다」</p>
                <p className="text-sm text-gray-600 mt-1">AI 시대의 새로운 비즈니스 패러다임</p>
                <a
                  href="https://search.shopping.naver.com/book/catalog/56947386015?cat_id=50005827&frm=PBOKMOD&query=%EA%B2%BD%EC%98%81%EC%BB%A8%EC%84%A4%ED%8C%85%EC%9D%80+%EB%81%9D%EB%82%AC%EB%8B%A4&NaPm=ct%3Dmg1m0sds%7Cci%3D8c87413a4bfd29d68d2d76afc282a9ce7a4306cc%7Ctr%3Dboknx%7Csn%3D95694%7Chk%3D1fa8c0e795bf3f5edb2f20fe467a07e57715e09b"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  📖 네이버 쇼핑에서 구매하기 ↗
                </a>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="card p-6 text-center">
              <Zap className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">5분 만에 완성</h3>
              <p className="text-gray-600">
                AI가 빠르게 분석하여 실행 가능한 비즈니스 가이드를 제공합니다.
              </p>
            </div>
            <div className="card p-6 text-center">
              <Target className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">90% 실행 가능</h3>
              <p className="text-gray-600">
                이론이 아닌 실제 실행할 수 있는 구체적인 가이드를 제공합니다.
              </p>
            </div>
            <div className="card p-6 text-center">
              <Brain className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">AI 기반 분석</h3>
              <p className="text-gray-600">
                최신 AI 기술로 시장 트렌드와 비즈니스 기회를 분석합니다.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="card p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">지금 시작하세요</h2>
            <p className="text-gray-600 mb-6">
              몇 가지 질문에 답하면 맞춤형 비즈니스 가이드를 받을 수 있습니다.
            </p>
            <button
              className="btn btn-primary px-8 py-3 text-lg"
              onClick={handleStartGuide}
            >
              비즈니스 가이드 생성하기
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-16 text-gray-500">
            <p>© 2025 AI Business Guide Maker. 경영컨설팅의 새로운 패러다임.</p>
          </div>
        </div>
      </div>
    );
  }

  // Form Page
  if (currentView === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={handleBackToLanding}
              className="btn btn-secondary mb-4"
            >
              ← 홈으로 돌아가기
            </button>
            <h1 className="text-3xl font-bold gradient-text mb-2">
              비즈니스 정보 입력
            </h1>
            <p className="text-gray-600">
              정확한 정보를 입력할수록 더 정밀한 가이드를 받을 수 있습니다.
            </p>
          </div>

          <BusinessForm
            onGuideGenerated={(guide) => {
              setGeneratedGuide(guide);
              setCurrentView('result');
            }}
          />
        </div>
      </div>
    );
  }

  // Result Page
  if (currentView === 'result') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-8">
          <GuideResult
            guide={generatedGuide}
            onBack={handleBackToForm}
            onRegenerate={handleRegenerate}
          />
        </div>
      </div>
    );
  }

  return null;
}

export default App;